import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTenant } from "../lib/tenant-context";

interface PINDevice {
  id: string;
  name: string;
  location: string;
  storeId: string;
  status: "online" | "offline";
  lastSeen: string;
  sessionsToday: number;
  takebacksToday: number;
}

interface RecentTakeback {
  id: string;
  customerName: string;
  itemCount: number;
  creditValue: number;
  status: "completed" | "pending" | "declined";
  converted: boolean;
  conversionValue?: number;
  pinName: string;
  createdAt: string;
}

interface DashboardStats {
  // Usage
  totalMinutesUsed: number;
  totalChats: number;
  totalTakebacks: number;
  activePins: number;
  totalPins: number;
  // Financial
  totalCreditPaidOut: number;
  totalConversionRevenue: number;
  totalInventoryValue: number;
  conversionRate: number;
  averageOrderValue: number;
}

interface ChartDataPoint {
  date: string;
  count: number;
}

type DateFilter = "7d" | "30d" | "90d" | "all";

export const Route = createFileRoute("/pin")({
  component: PINDashboard,
});

// Generate demo chart data for a given number of days
function generateDemoChartData(
  days: number,
  tenantId: string,
): ChartDataPoint[] {
  const data: ChartDataPoint[] = [];
  const now = new Date();
  const numDays = days === 0 ? 90 : days;

  for (let i = numDays - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);

    // Base count with some variation
    let baseCount = tenantId === "arcteryx" ? 3 : 5;

    // Add day-of-week variation (weekends slightly higher)
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      baseCount += tenantId === "arcteryx" ? 2 : 3;
    }

    // Add some randomness (use deterministic "random" based on date to avoid flickering)
    const seed = date.getDate() + date.getMonth() * 31;
    const variation = seed % (tenantId === "arcteryx" ? 4 : 6);
    const count = baseCount + variation;

    data.push({
      date: date.toISOString().split("T")[0],
      count,
    });
  }

  return data;
}

function PINDashboard() {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();

  // Generate initial chart data immediately to avoid "No data" flash
  const initialChartData = useMemo(
    () => generateDemoChartData(30, currentTenant.id),
    [currentTenant.id],
  );

  const [pins, setPins] = useState<PINDevice[]>([]);
  const [recentTakebacks, setRecentTakebacks] = useState<RecentTakeback[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalMinutesUsed: 0,
    totalChats: 0,
    totalTakebacks: 0,
    activePins: 0,
    totalPins: 0,
    totalCreditPaidOut: 0,
    totalConversionRevenue: 0,
    totalInventoryValue: 0,
    conversionRate: 0,
    averageOrderValue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] =
    useState<ChartDataPoint[]>(initialChartData);
  const [dateFilter, setDateFilter] = useState<DateFilter>("30d");
  const chartLoading = false; // Demo data loads synchronously
  const [syncStatus, setSyncStatus] = useState<{
    lastSyncAt: string | null;
    status: string;
    syncing: boolean;
  }>({ lastSyncAt: null, status: "unknown", syncing: false });

  useEffect(() => {
    loadDashboardData();
  }, [currentTenant.id]);

  useEffect(() => {
    // Update chart data when filter or tenant changes
    const days =
      dateFilter === "7d"
        ? 7
        : dateFilter === "30d"
          ? 30
          : dateFilter === "90d"
            ? 90
            : 90;
    setChartData(generateDemoChartData(days, currentTenant.id));
    // Also try to load real data from API
    loadChartData();
  }, [dateFilter, currentTenant.id]);

  const loadChartData = async () => {
    const days =
      dateFilter === "7d"
        ? 7
        : dateFilter === "30d"
          ? 30
          : dateFilter === "90d"
            ? 90
            : 90;
    try {
      const res = await fetch(`/api/v1/pin/takebacks/history?days=${days}`, {
        headers: { "X-Tenant-ID": currentTenant.id },
      });
      if (res.ok) {
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await res.json();
          if (data.history && data.history.length > 0) {
            setChartData(data.history);
            setSyncStatus((prev) => ({
              ...prev,
              lastSyncAt: data.lastSyncAt,
              status: data.syncStatus || "unknown",
            }));
            return;
          }
        }
      }
      // Use demo chart data if API doesn't return valid data
      setChartData(generateDemoChartData(days, currentTenant.id));
    } catch (error) {
      console.error("Failed to load chart data:", error);
      // Use demo chart data on error
      setChartData(generateDemoChartData(days, currentTenant.id));
    }
  };

  const handleSyncData = async () => {
    setSyncStatus((prev) => ({ ...prev, syncing: true }));
    try {
      const res = await fetch("/api/v1/pin/takebacks/sync", {
        method: "POST",
        headers: { "X-Tenant-ID": currentTenant.id },
      });
      if (res.ok) {
        // Reload chart data after sync
        await loadChartData();
      }
    } catch (error) {
      console.error("Failed to sync data:", error);
    } finally {
      setSyncStatus((prev) => ({ ...prev, syncing: false }));
    }
  };

  const loadDashboardData = async () => {
    try {
      // Fetch devices
      const devicesRes = await fetch("/api/v1/pin/devices", {
        headers: { "X-Tenant-ID": currentTenant.id },
      });
      let hasDevices = false;
      if (devicesRes.ok) {
        const data = await devicesRes.json();
        if (data.devices && data.devices.length > 0) {
          setPins(data.devices);
          hasDevices = true;
        }
      }

      // Fetch real takebacks
      const takebacksRes = await fetch("/api/v1/pin/takebacks?limit=10", {
        headers: { "X-Tenant-ID": currentTenant.id },
      });
      let hasTakebacks = false;
      if (takebacksRes.ok) {
        const data = await takebacksRes.json();
        if (data.takebacks && data.takebacks.length > 0) {
          setRecentTakebacks(data.takebacks);
          hasTakebacks = true;

          // Calculate real stats from takebacks
          const totalCredit = data.takebacks.reduce(
            (sum: number, tb: RecentTakeback) => sum + (tb.creditValue || 0),
            0,
          );
          const totalConversion = data.takebacks
            .filter((tb: RecentTakeback) => tb.converted)
            .reduce(
              (sum: number, tb: RecentTakeback) =>
                sum + (tb.conversionValue || 0),
              0,
            );
          const convertedCount = data.takebacks.filter(
            (tb: RecentTakeback) => tb.converted,
          ).length;

          setStats((prev) => ({
            ...prev,
            totalTakebacks: data.total || data.takebacks.length,
            totalCreditPaidOut: totalCredit,
            totalConversionRevenue: totalConversion,
            conversionRate:
              data.takebacks.length > 0
                ? Math.round((convertedCount / data.takebacks.length) * 100)
                : 0,
            averageOrderValue:
              convertedCount > 0
                ? Math.round(totalConversion / convertedCount)
                : 0,
          }));
        }
      }

      // If no real data, use demo data
      if (!hasDevices && !hasTakebacks) {
        setDemoData();
      } else if (!hasDevices) {
        setDemoPins();
      } else if (!hasTakebacks) {
        setDemoTakebacks();
      }
    } catch (error) {
      console.error("Failed to load dashboard:", error);
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const setDemoPins = () => {
    if (currentTenant.id === "arcteryx") {
      setPins([
        {
          id: "pin-van-001",
          name: "Vancouver Flagship",
          location: "Vancouver, BC",
          storeId: "arcteryx-vancouver",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 18,
          takebacksToday: 6,
        },
        {
          id: "pin-whi-001",
          name: "Whistler",
          location: "Whistler, BC",
          storeId: "arcteryx-whistler",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 24,
          takebacksToday: 9,
        },
        {
          id: "pin-sea-001",
          name: "Seattle",
          location: "Seattle, WA",
          storeId: "arcteryx-seattle",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 15,
          takebacksToday: 5,
        },
      ]);
    } else if (currentTenant.id === "nespresso") {
      // Nespresso recycling collection points
      setPins([
        {
          id: "pin-nes-nyc-001",
          name: "NYC Boutique",
          location: "New York, NY",
          storeId: "nespresso-nyc",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 156,
          takebacksToday: 142, // Capsule drop-offs
        },
        {
          id: "pin-nes-la-001",
          name: "Beverly Hills Boutique",
          location: "Los Angeles, CA",
          storeId: "nespresso-la",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 98,
          takebacksToday: 87,
        },
        {
          id: "pin-nes-chi-001",
          name: "Chicago Michigan Ave",
          location: "Chicago, IL",
          storeId: "nespresso-chi",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 72,
          takebacksToday: 65,
        },
        {
          id: "pin-nes-mia-001",
          name: "Miami Design District",
          location: "Miami, FL",
          storeId: "nespresso-mia",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 45,
          takebacksToday: 38,
        },
        {
          id: "pin-nes-sf-001",
          name: "San Francisco",
          location: "San Francisco, CA",
          storeId: "nespresso-sf",
          status: "offline",
          lastSeen: new Date(Date.now() - 7200000).toISOString(),
          sessionsToday: 0,
          takebacksToday: 0,
        },
      ]);
    } else if (currentTenant.id === "lego") {
      // LEGO brick collection and set trade-in points
      setPins([
        {
          id: "pin-lego-nyc-001",
          name: "LEGO Store Rockefeller",
          location: "New York, NY",
          storeId: "lego-nyc",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 156,
          takebacksToday: 48,
        },
        {
          id: "pin-lego-dis-001",
          name: "LEGO Store Disney Springs",
          location: "Orlando, FL",
          storeId: "lego-disney",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 234,
          takebacksToday: 72,
        },
        {
          id: "pin-lego-lax-001",
          name: "LEGO Store Downtown Disney",
          location: "Anaheim, CA",
          storeId: "lego-anaheim",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 189,
          takebacksToday: 56,
        },
        {
          id: "pin-lego-moa-001",
          name: "LEGO Store Mall of America",
          location: "Minneapolis, MN",
          storeId: "lego-moa",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 145,
          takebacksToday: 41,
        },
        {
          id: "pin-lego-lon-001",
          name: "LEGO Store Leicester Square",
          location: "London, UK",
          storeId: "lego-london",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 178,
          takebacksToday: 63,
        },
      ]);
    } else if (currentTenant.id === "adidas") {
      // adidas Infinite Play trade-in points
      setPins([
        {
          id: "pin-adidas-nyc-001",
          name: "adidas 5th Avenue NYC",
          location: "New York, NY",
          storeId: "str_adidas_nyc",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 52,
          takebacksToday: 19,
        },
        {
          id: "pin-adidas-la-001",
          name: "adidas Originals LA",
          location: "Los Angeles, CA",
          storeId: "str_adidas_la",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 38,
          takebacksToday: 14,
        },
        {
          id: "pin-adidas-chi-001",
          name: "adidas Chicago",
          location: "Chicago, IL",
          storeId: "str_adidas_chi",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 31,
          takebacksToday: 11,
        },
      ]);
    } else if (currentTenant.id === "nike") {
      // Nike Refurbished trade-in points
      setPins([
        {
          id: "pin-nyc-001",
          name: "House of Innovation NYC",
          location: "New York, NY",
          storeId: "nike-house-of-innovation",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 48,
          takebacksToday: 18,
        },
        {
          id: "pin-soho-001",
          name: "Nike SoHo",
          location: "New York, NY",
          storeId: "nike-soho",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 36,
          takebacksToday: 12,
        },
        {
          id: "pin-la-001",
          name: "Nike The Grove",
          location: "Los Angeles, CA",
          storeId: "nike-the-grove",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 42,
          takebacksToday: 15,
        },
        {
          id: "pin-chi-001",
          name: "Nike Michigan Avenue",
          location: "Chicago, IL",
          storeId: "nike-michigan-ave",
          status: "offline",
          lastSeen: new Date(Date.now() - 3600000).toISOString(),
          sessionsToday: 0,
          takebacksToday: 0,
        },
      ]);
    } else if (currentTenant.id === "demobrand") {
      // Demo Brand
      setPins([
        {
          id: "pin-dt-001",
          name: "Demo Brand Downtown",
          location: "Downtown",
          storeId: "demobrand-downtown",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 28,
          takebacksToday: 10,
        },
        {
          id: "pin-mall-001",
          name: "Demo Brand Mall Location",
          location: "Shopping Mall",
          storeId: "demobrand-mall",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 35,
          takebacksToday: 12,
        },
        {
          id: "pin-outlet-001",
          name: "Demo Brand Outlet",
          location: "Outlet Center",
          storeId: "demobrand-outlet",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 22,
          takebacksToday: 8,
        },
      ]);
    } else {
      // Lululemon
      setPins([
        {
          id: "pin-nj-001",
          name: "Short Hills Mall",
          location: "Short Hills, NJ",
          storeId: "lululemon-short-hills",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 24,
          takebacksToday: 8,
        },
        {
          id: "pin-nyc-001",
          name: "Fifth Avenue Flagship",
          location: "New York, NY",
          storeId: "lululemon-5th-ave",
          status: "online",
          lastSeen: new Date().toISOString(),
          sessionsToday: 42,
          takebacksToday: 15,
        },
        {
          id: "pin-la-001",
          name: "Santa Monica",
          location: "Los Angeles, CA",
          storeId: "lululemon-santa-monica",
          status: "offline",
          lastSeen: new Date(Date.now() - 3600000).toISOString(),
          sessionsToday: 18,
          takebacksToday: 6,
        },
      ]);
    }
  };

  const setDemoTakebacks = () => {
    if (currentTenant.id === "arcteryx") {
      setRecentTakebacks([
        {
          id: "tb-001",
          customerName: "Customer #10482",
          itemCount: 2,
          creditValue: 400,
          status: "completed",
          converted: true,
          conversionValue: 1100,
          pinName: "Vancouver Flagship",
          createdAt: new Date(Date.now() - 1800000).toISOString(),
        },
        {
          id: "tb-002",
          customerName: "Customer #10495",
          itemCount: 3,
          creditValue: 315,
          status: "completed",
          converted: true,
          conversionValue: 625,
          pinName: "Whistler",
          createdAt: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: "tb-003",
          customerName: "Customer #10503",
          itemCount: 1,
          creditValue: 95,
          status: "completed",
          converted: false,
          pinName: "Seattle",
          createdAt: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: "tb-004",
          customerName: "Guest",
          itemCount: 1,
          creditValue: 180,
          status: "declined",
          converted: false,
          pinName: "Vancouver Flagship",
          createdAt: new Date(Date.now() - 14400000).toISOString(),
        },
        {
          id: "tb-005",
          customerName: "Customer #10519",
          itemCount: 2,
          creditValue: 520,
          status: "completed",
          converted: true,
          conversionValue: 1200,
          pinName: "Whistler",
          createdAt: new Date(Date.now() - 21600000).toISOString(),
        },
      ]);
    } else if (currentTenant.id === "nespresso") {
      // Nespresso: capsule recycling sessions
      setRecentTakebacks([
        {
          id: "rec-001",
          customerName: "Customer #20147",
          itemCount: 45, // Capsules recycled
          creditValue: 90, // Points earned
          status: "completed",
          converted: true,
          conversionValue: 42, // Coffee purchase
          pinName: "NYC Boutique",
          createdAt: new Date(Date.now() - 900000).toISOString(),
        },
        {
          id: "rec-002",
          customerName: "Customer #20163",
          itemCount: 120,
          creditValue: 240,
          status: "completed",
          converted: true,
          conversionValue: 85,
          pinName: "Beverly Hills Boutique",
          createdAt: new Date(Date.now() - 2700000).toISOString(),
        },
        {
          id: "rec-003",
          customerName: "Customer #20178",
          itemCount: 30,
          creditValue: 60,
          status: "completed",
          converted: false,
          pinName: "Chicago Michigan Ave",
          createdAt: new Date(Date.now() - 5400000).toISOString(),
        },
        {
          id: "rec-004",
          customerName: "Customer #20192",
          itemCount: 75,
          creditValue: 150,
          status: "completed",
          converted: true,
          conversionValue: 65,
          pinName: "NYC Boutique",
          createdAt: new Date(Date.now() - 10800000).toISOString(),
        },
        {
          id: "rec-005",
          customerName: "Guest",
          itemCount: 50,
          creditValue: 100,
          status: "completed",
          converted: false,
          pinName: "Miami Design District",
          createdAt: new Date(Date.now() - 18000000).toISOString(),
        },
      ]);
    } else if (currentTenant.id === "lego") {
      // LEGO: brick and set trade-ins (IDs match database)
      setRecentTakebacks([
        {
          id: "pin_lego_ses_002",
          customerName: "Customer #30254",
          itemCount: 1, // Complete set
          creditValue: 145, // Star Wars Millennium Falcon
          status: "completed",
          converted: true,
          conversionValue: 289, // New set purchase
          pinName: "LEGO Times Square",
          createdAt: new Date(Date.now() - 720000).toISOString(),
        },
        {
          id: "pin_lego_ses_005",
          customerName: "Customer #30271",
          itemCount: 3, // Boxes of loose bricks (by weight)
          creditValue: 84, // ~7 lbs @ $12/lb
          status: "completed",
          converted: true,
          conversionValue: 159,
          pinName: "LEGO Mall of America",
          createdAt: new Date(Date.now() - 2100000).toISOString(),
        },
        {
          id: "pin_lego_ses_007",
          customerName: "Customer #30289",
          itemCount: 2, // Two complete sets
          creditValue: 210, // Technic + Architecture sets
          status: "completed",
          converted: false,
          pinName: "LEGO Downtown Disney",
          createdAt: new Date(Date.now() - 4500000).toISOString(),
        },
      ]);
    } else if (currentTenant.id === "adidas") {
      // adidas Infinite Play: footwear and apparel trade-ins
      setRecentTakebacks([
        {
          id: "tb-001",
          customerName: "Customer #40291",
          itemCount: 2,
          creditValue: 72,
          status: "completed",
          converted: true,
          conversionValue: 180,
          pinName: "adidas 5th Avenue NYC",
          createdAt: new Date(Date.now() - 1800000).toISOString(),
        },
        {
          id: "tb-002",
          customerName: "Customer #40318",
          itemCount: 3,
          creditValue: 93,
          status: "completed",
          converted: true,
          conversionValue: 220,
          pinName: "adidas Originals LA",
          createdAt: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: "tb-003",
          customerName: "Customer #40325",
          itemCount: 1,
          creditValue: 28,
          status: "completed",
          converted: false,
          pinName: "adidas 5th Avenue NYC",
          createdAt: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: "tb-004",
          customerName: "Customer #40337",
          itemCount: 2,
          creditValue: 64,
          status: "completed",
          converted: true,
          conversionValue: 160,
          pinName: "adidas Chicago",
          createdAt: new Date(Date.now() - 10800000).toISOString(),
        },
        {
          id: "tb-005",
          customerName: "Guest",
          itemCount: 1,
          creditValue: 45,
          status: "declined",
          converted: false,
          pinName: "adidas Originals LA",
          createdAt: new Date(Date.now() - 14400000).toISOString(),
        },
        {
          id: "tb-006",
          customerName: "Customer #40352",
          itemCount: 4,
          creditValue: 142,
          status: "completed",
          converted: true,
          conversionValue: 320,
          pinName: "adidas 5th Avenue NYC",
          createdAt: new Date(Date.now() - 21600000).toISOString(),
        },
        {
          id: "tb-007",
          customerName: "Customer #40371",
          itemCount: 2,
          creditValue: 85,
          status: "completed",
          converted: true,
          conversionValue: 195,
          pinName: "adidas Chicago",
          createdAt: new Date(Date.now() - 28800000).toISOString(),
        },
      ]);
    } else if (currentTenant.id === "nike") {
      // Nike Refurbished: sneaker and apparel trade-ins
      setRecentTakebacks([
        {
          id: "tb-001",
          customerName: "Customer #50412",
          itemCount: 2,
          creditValue: 103,
          status: "completed",
          converted: true,
          conversionValue: 180,
          pinName: "House of Innovation NYC",
          createdAt: new Date(Date.now() - 1800000).toISOString(),
        },
        {
          id: "tb-002",
          customerName: "Customer #50428",
          itemCount: 3,
          creditValue: 141,
          status: "completed",
          converted: true,
          conversionValue: 280,
          pinName: "Nike SoHo",
          createdAt: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: "tb-003",
          customerName: "Customer #50441",
          itemCount: 1,
          creditValue: 39,
          status: "completed",
          converted: false,
          pinName: "House of Innovation NYC",
          createdAt: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: "tb-004",
          customerName: "Guest",
          itemCount: 1,
          creditValue: 54,
          status: "declined",
          converted: false,
          pinName: "Nike The Grove",
          createdAt: new Date(Date.now() - 14400000).toISOString(),
        },
        {
          id: "tb-005",
          customerName: "Customer #50457",
          itemCount: 4,
          creditValue: 186,
          status: "completed",
          converted: true,
          conversionValue: 320,
          pinName: "Nike SoHo",
          createdAt: new Date(Date.now() - 21600000).toISOString(),
        },
      ]);
    } else if (currentTenant.id === "demobrand") {
      // Demo Brand trade-ins
      setRecentTakebacks([
        {
          id: "tb-001",
          customerName: "Customer #60103",
          itemCount: 2,
          creditValue: 115,
          status: "completed",
          converted: true,
          conversionValue: 220,
          pinName: "Demo Brand Downtown",
          createdAt: new Date(Date.now() - 1800000).toISOString(),
        },
        {
          id: "tb-002",
          customerName: "Customer #60118",
          itemCount: 3,
          creditValue: 80,
          status: "completed",
          converted: true,
          conversionValue: 165,
          pinName: "Demo Brand Mall Location",
          createdAt: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: "tb-003",
          customerName: "Customer #60125",
          itemCount: 1,
          creditValue: 20,
          status: "completed",
          converted: false,
          pinName: "Demo Brand Downtown",
          createdAt: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: "tb-004",
          customerName: "Guest",
          itemCount: 1,
          creditValue: 35,
          status: "declined",
          converted: false,
          pinName: "Demo Brand Outlet",
          createdAt: new Date(Date.now() - 14400000).toISOString(),
        },
        {
          id: "tb-005",
          customerName: "Customer #60139",
          itemCount: 4,
          creditValue: 145,
          status: "completed",
          converted: true,
          conversionValue: 280,
          pinName: "Demo Brand Mall Location",
          createdAt: new Date(Date.now() - 21600000).toISOString(),
        },
      ]);
    } else {
      // Lululemon
      setRecentTakebacks([
        {
          id: "tb-001",
          customerName: "Customer #70201",
          itemCount: 2,
          creditValue: 45,
          status: "completed",
          converted: true,
          conversionValue: 118,
          pinName: "Short Hills Mall",
          createdAt: new Date(Date.now() - 1800000).toISOString(),
        },
        {
          id: "tb-002",
          customerName: "Customer #70218",
          itemCount: 3,
          creditValue: 72,
          status: "completed",
          converted: true,
          conversionValue: 196,
          pinName: "Fifth Avenue",
          createdAt: new Date(Date.now() - 3600000).toISOString(),
        },
        {
          id: "tb-003",
          customerName: "Customer #70229",
          itemCount: 1,
          creditValue: 35,
          status: "completed",
          converted: false,
          pinName: "Short Hills Mall",
          createdAt: new Date(Date.now() - 7200000).toISOString(),
        },
        {
          id: "tb-004",
          customerName: "Guest",
          itemCount: 1,
          creditValue: 25,
          status: "declined",
          converted: false,
          pinName: "Santa Monica",
          createdAt: new Date(Date.now() - 14400000).toISOString(),
        },
        {
          id: "tb-005",
          customerName: "Customer #70245",
          itemCount: 4,
          creditValue: 98,
          status: "completed",
          converted: true,
          conversionValue: 234,
          pinName: "Fifth Avenue",
          createdAt: new Date(Date.now() - 21600000).toISOString(),
        },
      ]);
    }
  };

  const setDemoData = () => {
    setDemoPins();
    setDemoTakebacks();

    // Demo stats - tenant-specific
    if (currentTenant.id === "arcteryx") {
      setStats({
        totalMinutesUsed: 1245,
        totalChats: 186,
        totalTakebacks: 47,
        activePins: 3,
        totalPins: 3,
        totalCreditPaidOut: 18650,
        totalConversionRevenue: 52400,
        totalInventoryValue: 46500,
        conversionRate: 72,
        averageOrderValue: 875,
      });
    } else if (currentTenant.id === "nespresso") {
      // Nespresso: capsule recycling metrics
      setStats({
        totalMinutesUsed: 892,
        totalChats: 156,
        totalTakebacks: 1847, // Capsule recycling sessions
        activePins: 4,
        totalPins: 5,
        totalCreditPaidOut: 4620, // Points value
        totalConversionRevenue: 8940, // Coffee/accessory purchases
        totalInventoryValue: 2800, // Aluminum value
        conversionRate: 45,
        averageOrderValue: 48,
      });
    } else if (currentTenant.id === "lego") {
      // LEGO: brick and set trade-in metrics
      setStats({
        totalMinutesUsed: 3456,
        totalChats: 423,
        totalTakebacks: 2341,
        activePins: 5,
        totalPins: 5,
        totalCreditPaidOut: 34200,
        totalConversionRevenue: 89400,
        totalInventoryValue: 125600,
        conversionRate: 78,
        averageOrderValue: 145,
      });
    } else if (currentTenant.id === "adidas") {
      // adidas Infinite Play: footwear and apparel trade-in metrics
      setStats({
        totalMinutesUsed: 2480,
        totalChats: 318,
        totalTakebacks: 156,
        activePins: 3,
        totalPins: 3,
        totalCreditPaidOut: 11240,
        totalConversionRevenue: 31500,
        totalInventoryValue: 24800,
        conversionRate: 74,
        averageOrderValue: 205,
      });
    } else if (currentTenant.id === "nike") {
      // Nike Refurbished: sneaker trade-in metrics
      setStats({
        totalMinutesUsed: 2156,
        totalChats: 284,
        totalTakebacks: 127,
        activePins: 3,
        totalPins: 4,
        totalCreditPaidOut: 8940,
        totalConversionRevenue: 24680,
        totalInventoryValue: 19850,
        conversionRate: 71,
        averageOrderValue: 195,
      });
    } else if (currentTenant.id === "demobrand") {
      // Demo Brand: Athletic apparel trade-in metrics
      setStats({
        totalMinutesUsed: 1650,
        totalChats: 245,
        totalTakebacks: 95,
        activePins: 3,
        totalPins: 3,
        totalCreditPaidOut: 6850,
        totalConversionRevenue: 18500,
        totalInventoryValue: 15200,
        conversionRate: 73,
        averageOrderValue: 175,
      });
    } else {
      // Lululemon: Credit is ~40% of retail, so inventory value should be ~2.5x credit paid
      setStats({
        totalMinutesUsed: 1847,
        totalChats: 312,
        totalTakebacks: 89,
        activePins: 2,
        totalPins: 3,
        totalCreditPaidOut: 4235,
        totalConversionRevenue: 12680,
        totalInventoryValue: 10890,
        conversionRate: 68,
        averageOrderValue: 156,
      });
    }
  };

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-light mb-2" style={{ color: "#171717" }}>
            Physical Intelligence
          </h1>
          <p className="text-sm" style={{ color: "#A9A9A9" }}>
            Monitor in-store kiosks, track trade-ins, and measure conversion
            performance
          </p>
        </div>
        <Link
          to="/pin/usage"
          className="px-4 py-2 rounded-full text-sm font-medium"
          style={{
            backgroundColor: "#FAFAFA",
            color: "#171717",
            border: "1px solid rgba(169,169,169,0.3)",
          }}
        >
          View Usage & Billing
        </Link>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <div
            className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4"
            style={{ borderColor: "#00FBA9" }}
          />
        </div>
      ) : (
        <>
          {/* Usage Stats */}
          <div className="mb-8">
            <h2
              className="text-xs font-medium tracking-wider mb-4"
              style={{ color: "#A9A9A9" }}
            >
              USAGE
            </h2>
            <div className="grid grid-cols-5 gap-4">
              <StatCard
                label="Active PINs"
                value={stats.activePins}
                suffix={`/ ${stats.totalPins}`}
                indicator={
                  stats.activePins === stats.totalPins ? "positive" : "neutral"
                }
              />
              <StatCard
                label="Minutes Used"
                value={stats.totalMinutesUsed.toLocaleString()}
                suffix="min"
              />
              <StatCard label="Total Chats" value={stats.totalChats} />
              <StatCard label="Total Takebacks" value={stats.totalTakebacks} />
              <StatCard
                label="Conversion Rate"
                value={stats.conversionRate}
                suffix="%"
                indicator="positive"
              />
            </div>
          </div>

          {/* Financial Metrics */}
          <div className="mb-8">
            <h2
              className="text-xs font-medium tracking-wider mb-4"
              style={{ color: "#A9A9A9" }}
            >
              FINANCIAL
            </h2>
            <div className="grid grid-cols-4 gap-4">
              <StatCard
                label="Credit Paid Out"
                value={`$${stats.totalCreditPaidOut.toLocaleString()}`}
                subtext="Total trade-in credits issued"
              />
              <StatCard
                label="Conversion Revenue"
                value={`$${stats.totalConversionRevenue.toLocaleString()}`}
                subtext="Sales from trade-in credits"
                indicator="positive"
              />
              <StatCard
                label="Inventory Value"
                value={`$${stats.totalInventoryValue.toLocaleString()}`}
                subtext="Value of collected items"
              />
              <StatCard
                label="Avg Order Value"
                value={`$${stats.averageOrderValue}`}
                subtext="From converted takebacks"
              />
            </div>
          </div>

          {/* Volume Trend Chart */}
          <div
            className="mb-8 bg-white border p-6"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Volume Trend
                </h2>
                <p className="text-sm mt-1" style={{ color: "#A9A9A9" }}>
                  {chartData.length > 0 ? (
                    <>
                      {new Date(chartData[0].date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                      {" — "}
                      {new Date(
                        chartData[chartData.length - 1].date,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                      {" · "}
                      {chartData
                        .reduce((sum, d) => sum + d.count, 0)
                        .toLocaleString()}{" "}
                      total takebacks
                    </>
                  ) : (
                    "Takeback events received over time"
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Sync status and button */}
                <div className="flex items-center gap-2">
                  {syncStatus.lastSyncAt && (
                    <span className="text-xs" style={{ color: "#A9A9A9" }}>
                      Last sync:{" "}
                      {new Date(syncStatus.lastSyncAt).toLocaleTimeString()}
                    </span>
                  )}
                  <button
                    onClick={handleSyncData}
                    disabled={syncStatus.syncing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors"
                    style={{
                      backgroundColor: syncStatus.syncing
                        ? "#FAFAFA"
                        : "#171717",
                      color: syncStatus.syncing ? "#A9A9A9" : "#FFFFFF",
                    }}
                  >
                    <svg
                      className={`w-3.5 h-3.5 ${syncStatus.syncing ? "animate-spin" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {syncStatus.syncing ? "Syncing..." : "Sync"}
                  </button>
                </div>
                {/* Date filter buttons */}
                <div
                  className="flex gap-1 p-1"
                  style={{ backgroundColor: "#FAFAFA" }}
                >
                  {(["7d", "30d", "90d", "all"] as const).map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setDateFilter(filter)}
                      className="px-3 py-1.5 text-sm rounded-full transition-colors"
                      style={{
                        backgroundColor:
                          dateFilter === filter ? "#171717" : "transparent",
                        color: dateFilter === filter ? "#FFFFFF" : "#A9A9A9",
                      }}
                    >
                      {filter === "all" ? "All" : filter.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {chartLoading ? (
              <div className="h-64 flex items-center justify-center">
                <div
                  className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
                  style={{ borderColor: "#00FBA9" }}
                />
              </div>
            ) : chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <p className="text-sm" style={{ color: "#A9A9A9" }}>
                  No takeback data for this period
                </p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer
                  width="100%"
                  height="100%"
                  minWidth={100}
                  minHeight={100}
                >
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="takebackGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#00FBA9"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#00FBA9"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: "#A9A9A9" }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        });
                      }}
                      interval={Math.max(0, Math.floor(chartData.length / 8))}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: "#A9A9A9" }}
                      width={60}
                      label={{
                        value: "Takebacks Received",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                          textAnchor: "middle",
                          fontSize: 11,
                          fill: "#A9A9A9",
                        },
                        offset: 10,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#171717",
                        border: "none",
                        borderRadius: "8px",
                        color: "#FFFFFF",
                        fontSize: "12px",
                      }}
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      }
                      formatter={(value) => [value ?? 0, "Takebacks"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#00FBA9"
                      strokeWidth={2}
                      fill="url(#takebackGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-3 gap-6">
            {/* Recent Takebacks Table */}
            <div
              className="col-span-2 bg-white border"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b flex justify-between items-center"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  Recent Takebacks
                </h2>
                <Link
                  to="/pin/$id"
                  params={{ id: "pin-nj-001" }}
                  className="text-sm"
                  style={{ color: "#00FBA9" }}
                >
                  View all
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr
                      className="border-b"
                      style={{ borderColor: "rgba(169,169,169,0.1)" }}
                    >
                      <th
                        className="text-left text-xs font-medium px-6 py-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        CUSTOMER
                      </th>
                      <th
                        className="text-left text-xs font-medium px-4 py-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        LOCATION
                      </th>
                      <th
                        className="text-right text-xs font-medium px-4 py-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        ITEMS
                      </th>
                      <th
                        className="text-right text-xs font-medium px-4 py-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        CREDIT
                      </th>
                      <th
                        className="text-center text-xs font-medium px-4 py-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        CONVERTED
                      </th>
                      <th
                        className="text-right text-xs font-medium px-6 py-3"
                        style={{ color: "#A9A9A9" }}
                      >
                        SALE VALUE
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTakebacks.map((tb) => (
                      <tr
                        key={tb.id}
                        onClick={() =>
                          navigate({
                            to: "/pin/takeback/$id",
                            params: { id: tb.id },
                          })
                        }
                        className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                        style={{ borderColor: "rgba(169,169,169,0.05)" }}
                      >
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: "#FAFAFA" }}
                            >
                              <span
                                className="text-sm"
                                style={{ color: "#171717" }}
                              >
                                {tb.customerName[0]}
                              </span>
                            </div>
                            <div>
                              <p
                                className="text-sm font-medium"
                                style={{ color: "#171717" }}
                              >
                                {tb.customerName}
                              </p>
                              <p
                                className="text-xs"
                                style={{ color: "#A9A9A9" }}
                              >
                                {formatTimeAgo(tb.createdAt)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-4 py-3 text-sm"
                          style={{ color: "#A9A9A9" }}
                        >
                          {tb.pinName}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-right"
                          style={{ color: "#171717" }}
                        >
                          {tb.itemCount}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-right font-medium"
                          style={{ color: "#00FBA9" }}
                        >
                          ${tb.creditValue}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {tb.status === "declined" ? (
                            <span
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                              style={{ backgroundColor: "rgba(255,76,0,0.15)" }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#FF4C00"
                                strokeWidth="2"
                              >
                                <path
                                  d="M18 6L6 18M6 6l12 12"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </span>
                          ) : tb.converted ? (
                            <span
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                              style={{
                                backgroundColor: "rgba(0,251,169,0.15)",
                              }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#00FBA9"
                                strokeWidth="2"
                              >
                                <path
                                  d="M20 6L9 17l-5-5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                              style={{
                                backgroundColor: "rgba(169,169,169,0.15)",
                              }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#A9A9A9"
                                strokeWidth="2"
                              >
                                <path
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </span>
                          )}
                        </td>
                        <td
                          className="px-6 py-3 text-sm text-right font-medium"
                          style={{
                            color: tb.converted ? "#171717" : "#A9A9A9",
                          }}
                        >
                          {tb.converted ? `$${tb.conversionValue}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PIN Devices List */}
            <div
              className="bg-white border"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="px-6 py-4 border-b"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                <h2 className="font-medium" style={{ color: "#171717" }}>
                  PIN Devices
                </h2>
              </div>
              <div
                className="divide-y"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                {pins.map((pin) => (
                  <Link
                    key={pin.id}
                    to="/pin/$id"
                    params={{ id: pin.id }}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor:
                          pin.status === "online" ? "#00FBA9" : "#A9A9A9",
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-medium text-sm"
                        style={{ color: "#171717" }}
                      >
                        {pin.name}
                      </p>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>
                        {pin.location}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className="text-sm font-medium"
                        style={{ color: "#171717" }}
                      >
                        {pin.takebacksToday}
                      </p>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>
                        today
                      </p>
                    </div>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#A9A9A9"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  suffix,
  subtext,
  indicator,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  subtext?: string;
  indicator?: "positive" | "negative" | "neutral";
}) {
  return (
    <div
      className="bg-white border p-4"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <p
        className="text-xs uppercase tracking-wider mb-2"
        style={{ color: "#A9A9A9" }}
      >
        {label}
      </p>
      <p
        className="text-2xl font-light"
        style={{ color: indicator === "positive" ? "#00FBA9" : "#171717" }}
      >
        {value}
        {suffix && (
          <span className="text-sm ml-1" style={{ color: "#A9A9A9" }}>
            {suffix}
          </span>
        )}
      </p>
      {subtext && (
        <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
          {subtext}
        </p>
      )}
    </div>
  );
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
